/**
 * The "Customized Metal Solutions for <industry>" section of a Ryerson industry page: a heading,
 * an optional named sales contact, an introduction, and an embedded ThingLink interactive image
 * (`https://www.thinglink.com/card/<id>`) with a caption and a button to the contact form. It
 * renders `./Mobile/CustomizedMetalSolutions.tsx` below `lg` and
 * `./Desktop/CustomizedMetalSolutions.tsx` from `lg` up; both read the same props.
 *
 * Position: an `AllSections` entry on five industry pages under
 * `src/pages/metal-solutions/industries/`: `aerospace-defense-semiconductor`,
 * `electric-vehicles`, `heavy-equipment`, `industrial-automation` and
 * `transportation/trailer-manufacturing`. Each passes `CustomizedMetalSolutionsContent[language]`
 * from its module in `src/content/ryerson/metal-solutions/industries/` (industrial automation reads
 * `packaging-and-processing-equipment.ts`). The page builds the section's `AllSections` title, and
 * so its anchor id, from `sectionHeading + " " + title` (industrial automation uses `title` alone).
 *
 * Not to be confused with `components/Shared/CustomizedMetalSolutions/` (the hero of the
 * Customized Metal Solutions page) or `components/CustomizedMetalSolutions/`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import CustomizedMetalSolutionsMobile from "./Mobile/CustomizedMetalSolutions";
import CustomizedMetalSolutionsDesktop from "./Desktop/CustomizedMetalSolutions";

/** The industry's sales contact, and the label of the contact-form button. */
type Contact = {
	/** Mobile only: heading of the contact box ("Contact your Heavy Equipment Professional"). */
	heading: string;
	/**
	 * Contact's name. Desktop shows the contact block only when this is non-empty; mobile always
	 * renders the box. The industrial automation copy leaves every field but `button` empty.
	 */
	name: string;
	/** Job title. Mobile starts a new line at each em dash (`—`). */
	title: string;
	/** Shown as a `mailto:` link. */
	email: string;
	/** Portrait image URL, drawn as a round CSS background. */
	portrait: string;
	/** Label of the button that goes to `/contact-us#productQuestion`. */
	button: string;
};

/** The embedded ThingLink card and its caption. */
type Thinglink = {
	/** Caption shown with the embed. */
	description: string;
	/** ThingLink card id; the iframe loads `https://www.thinglink.com/card/<id>`. */
	id: string;
	/** Not read by either layout. */
	height: number;
	/** Not read by either layout. */
	width: number;
	/**
	 * Source image height in pixels. With `originalWidth` it sets the desktop iframe's aspect
	 * ratio; both layouts also write it to `data-original-height`.
	 */
	originalHeight: number;
	/** Source image width in pixels; see `originalHeight`. */
	originalWidth: number;
};

/** Copy for one language of the section; content modules type their objects with it. */
export interface CustomizedMetalSolutionsProps {
	/** The industry name; desktop shows it as the 150 px title under `sectionHeading`. */
	title: string;
	/** Desktop only: the line above `title`, for example "Customized Metal Solutions for". */
	sectionHeading: string;
	/** Introductory paragraph. */
	mainDescription: string;
	contact: Contact;
	thinglink: Thinglink;
}

/** Renders the section for the current breakpoint; see the file header. */
const CustomizedMetalSolutions: React.FC<CustomizedMetalSolutionsProps> = (props) => {
	// Adds ThingLink's `responsive.js` only when an element with id `thinglink_script` already
	// exists. Nothing in this repository creates one, so the script is not added. The desktop
	// layout sizes its iframe itself; the mobile one sets only `width: 100%`.
	React.useEffect(() => {
		if (typeof document !== "undefined" && document.getElementById("thinglink_script")) {
			var script = document.createElement("script");
			script.id = "thinglink_script";
			script.src = "//cdn.thinglink.me/jse/responsive.js";
			script.async = true;
			document.body.appendChild(script);
		}
	}, []);

	return (
		<>
			<Media lessThan="lg">
				<CustomizedMetalSolutionsMobile {...props} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<CustomizedMetalSolutionsDesktop {...props} />
			</Media>
		</>
	);
};

export default CustomizedMetalSolutions;
